import { useState, useEffect } from "react";
import { motion } from 'framer-motion';
import { Link } from "react-router-dom";
import Logo from "components/Logo";
import { Menu, X, LogIn, UserPlus } from "lucide-react";


const Navbar = () => {

  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }

  const menuVariants = {
    open: { x: 0, transition: { type: 'spring', stiffness: 100 } },
    closed: { x: '100%', transition: { type: 'spring', stiffness: 100 } },
  };

  return (
    <>
      {/* Desktop Navigation */}
      <header className={`hidden md:block fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-lg shadow-sm border-b border-gray-100'
          : 'bg-white/90 backdrop-blur-sm'
      }`}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center h-16">
            {/* Logo Section */}
            <div className="flex items-center space-x-8">
              <Logo to="/" />
              <div className="flex items-center space-x-4">
                <Link
                  to="/login/customer"
                  className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-primary hover:bg-gray-50 rounded-md transition-all"
                >
                  <LogIn size={16} />
                  <span>Bireysel</span>
                </Link>
                <Link
                  to="/login/reseller"
                  className="flex items-center space-x-2 px-4 py-2 text-sm font-medium bg-primary text-white rounded-md hover:bg-primary/90 hover:shadow-md transition-all"
                >
                  <UserPlus size={16} />
                  <span>Kurumsal</span>
                </Link>
              </div>
            </div>

            {/* Navigation Menu */}
            <nav className="flex items-center space-x-6">
              <Link
                to="/"
                className="relative text-primary font-medium hover:text-primary/80 transition-colors after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-primary after:scale-x-100"
              >
                Anasayfa
              </Link>
              <Link
                to="/banka"
                className="relative text-gray-700 font-medium hover:text-primary transition-colors after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-primary after:scale-x-0 hover:after:scale-x-100 after:transition-transform"
              >
                Banka
              </Link>
              <Link
                to="/iletisim"
                className="relative text-gray-700 font-medium hover:text-primary transition-colors after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-primary after:scale-x-0 hover:after:scale-x-100 after:transition-transform"
              >
                İletişim
              </Link>
              <Link
                to="/bayilik-basvurusu"
                className="relative text-gray-700 font-medium hover:text-primary transition-colors after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-primary after:scale-x-0 hover:after:scale-x-100 after:transition-transform"
              >
                Bayilik
              </Link>
              <a
                href="#sss"
                className="relative text-gray-700 font-medium hover:text-primary transition-colors after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-primary after:scale-x-0 hover:after:scale-x-100 after:transition-transform"
              >
                SSS
              </a>
            </nav>
          </div>
        </div>
      </header>

      {/* Mobile Navigation */}
      <header className={`block md:hidden fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-lg shadow-sm'
          : 'bg-white/90 backdrop-blur-sm'
      }`}>
        <div className="px-4">
          <div className="flex justify-between items-center h-16">
            {/* Logo and Login Buttons */}
            <div className="flex items-center space-x-4">
              <Logo isMobile to="/" />
              <div className="flex items-center space-x-3">
                <Link
                  to="/login/customer"
                  className="flex items-center space-x-1.5 px-3 py-2 text-sm font-medium text-gray-600 hover:text-primary hover:bg-gray-50 rounded-md transition-all"
                >
                  <LogIn size={16} />
                  <span>Bireysel</span>
                </Link>
                <Link
                  to="/login/reseller"
                  className="flex items-center space-x-1.5 px-3 py-2 text-sm font-medium bg-primary text-white rounded-md hover:bg-primary/90 transition-all"
                >
                  <UserPlus size={16} />
                  <span>Kurumsal</span>
                </Link>
              </div>
            </div>

            {/* Menu Toggle */}
            <button
              onClick={toggleMenu}
              className="p-2 text-gray-600 hover:text-primary hover:bg-gray-50 rounded-md transition-all"
            >
              {isOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <motion.div
          className="fixed top-16 right-0 w-80 h-screen bg-white-100 shadow-xl border-l border-gray-200 z-40"
          initial="closed"
          animate={isOpen ? 'open' : 'closed'}
          variants={menuVariants}
        >
          <nav className="p-6 space-y-6">
            <Link
              to="/"
              className="block text-lg font-semibold text-primary border-b border-primary/20 pb-4"
              onClick={toggleMenu}
            >
              Anasayfa
            </Link>
            <Link
              to="/banka"
              className="block text-lg font-medium text-gray-700 hover:text-primary hover:bg-gray-50 rounded-lg px-3 py-3 transition-all"
              onClick={toggleMenu}
            >
              Banka Hesapları
            </Link>
            <Link
              to="/iletisim"
              className="block text-lg font-medium text-gray-700 hover:text-primary hover:bg-gray-50 rounded-lg px-3 py-3 transition-all"
              onClick={toggleMenu}
            >
              İletişim
            </Link>
            <Link
              to="/bayilik-basvurusu"
              className="block text-lg font-medium text-gray-700 hover:text-primary hover:bg-gray-50 rounded-lg px-3 py-3 transition-all"
              onClick={toggleMenu}
            >
              Bayilik Başvurusu
            </Link>
            <a
              href="#sss"
              className="block text-lg font-medium text-gray-700 hover:text-primary hover:bg-gray-50 rounded-lg px-3 py-3 transition-all"
              onClick={toggleMenu}
            >
              SSS
            </a>
          </nav>
        </motion.div>
      </header>
    </>
  )
}

export default Navbar;